import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Package,
  Truck,
  CheckCircle,
  AlertTriangle,
  Users,
} from "lucide-react";

interface DashboardStats {
  todayReceived: number;
  pendingInspection: number;
  inspectionPassed: number;
  inspectionFailed: number;
  readyToShip: number;
}

interface PackageData {
  id: string;
  tracking_number: string;
  current_status: string;
  created_at: string;
  shelf_location?: string;
}

function AdminDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [packages, setPackages] = useState<PackageData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    checkAuthAndLoadData();
  }, []);

  const checkAuthAndLoadData = async () => {
    const adminToken = localStorage.getItem("admin_token");
    const adminUser = localStorage.getItem("admin_user");

    if (!adminToken || !adminUser) {
      navigate("/admin/login");
      return;
    }

    try {
      await loadDashboardData();
    } catch (err) {
      console.error("Auth check error:", err);
      navigate("/admin/login");
    }
  };

  const loadDashboardData = async () => {
    const adminToken = localStorage.getItem("admin_token");
    if (!adminToken) return;

    try {
      setLoading(true);

      const API_BASE = import.meta.env.DEV
        ? "http://localhost:8787"
        : "https://shopglobal-backend-production.finhub.workers.dev";

      const [statsResponse, packagesResponse] = await Promise.all([
        fetch(`${API_BASE}/api/admin/dashboard/stats`, {
          headers: {
            Authorization: `Bearer ${adminToken}`,
            "Content-Type": "application/json",
          },
        }),
        fetch(`${API_BASE}/api/admin/dashboard/packages`, {
          headers: {
            Authorization: `Bearer ${adminToken}`,
            "Content-Type": "application/json",
          },
        }),
      ]);

      const statsData = await statsResponse.json();
      const packagesData = await packagesResponse.json();

      if (statsData.success) {
        setStats(statsData.stats);
      }

      if (packagesData.success) {
        setPackages(packagesData.packages);
      }
    } catch (err) {
      console.error("Dashboard load error:", err);
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      received_at_warehouse: "bg-blue-100 text-blue-800",
      inspection_passed: "bg-green-100 text-green-800",
      inspection_failed: "bg-red-100 text-red-800",
      stored: "bg-gray-100 text-gray-800",
      prepared_for_shipping: "bg-purple-100 text-purple-800",
    };

    const displayStatus = status
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[status] || "bg-gray-100 text-gray-800"}`}>
        {displayStatus}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading warehouse dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Package className="h-8 w-8 text-orange-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Warehouse Operations
                </h1>
                <p className="text-gray-600">ShopGlobal Pro Dashboard</p>
              </div>
            </div>
            <button
              onClick={() => {
                localStorage.removeItem("admin_token");
                localStorage.removeItem("admin_user");
                navigate("/admin/login");
              }}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 text-sm">
            {error}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Truck className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Today Received
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.todayReceived || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <AlertTriangle className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Pending Inspection
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.pendingInspection || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Inspection Passed
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.inspectionPassed || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <AlertTriangle className="h-8 w-8 text-red-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Inspection Failed
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.inspectionFailed || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <Package className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  Ready to Ship
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.readyToShip || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Packages Requiring Attention */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Packages Requiring Attention
            </h3>
            <p className="text-sm text-gray-600">
              Recent packages needing inspection or processing
            </p>
          </div>
          <div className="divide-y divide-gray-200">
            {packages.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-500">
                No packages requiring attention at this time
              </div>
            ) : (
              packages.map((pkg) => (
                <div
                  key={pkg.id}
                  className="px-6 py-4 flex justify-between items-center hover:bg-gray-50">
                  <div>
                    <div className="flex items-center">
                      <p className="text-sm font-medium text-gray-900">
                        {pkg.tracking_number}
                      </p>
                      {pkg.shelf_location && (
                        <span className="ml-2 text-xs text-gray-500">
                          Shelf: {pkg.shelf_location}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      Created: {new Date(pkg.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    {getStatusBadge(pkg.current_status)}
                    <button className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors">
                      Process
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6 text-center hover:shadow-lg transition-shadow cursor-pointer">
            <Package className="h-12 w-12 text-orange-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Receive Package
            </h3>
            <p className="text-sm text-gray-600">
              Scan tracking number and record receipt
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6 text-center hover:shadow-lg transition-shadow cursor-pointer">
            <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Quality Inspection
            </h3>
            <p className="text-sm text-gray-600">
              Check package condition and contents
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6 text-center hover:shadow-lg transition-shadow cursor-pointer">
            <Truck className="h-12 w-12 text-blue-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Prepare for Shipping
            </h3>
            <p className="text-sm text-gray-600">
              Bundle packages and assign to carrier
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboardPage;
